import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/** Shopify Admin API — does the CRM see the full order history (read_all_orders) or only the last 60 days? */
export const ShopifyAccessCard = () => {
  const qc = useQueryClient();
  const { data: s, isFetching } = useQuery({
    queryKey: ["shopify-admin-status"],
    queryFn: async () => (await api.get("/shopify/admin-status")).data,
    staleTime: 60_000,
  });
  const recheck = async () => {
    try {
      const r = await api.get("/shopify/admin-status", { params: { refresh: 1 } });
      qc.setQueryData(["shopify-admin-status"], r.data);
      if (r.data?.error) toast.error(r.data.error);
      else toast.success(r.data?.all_orders ? "Full order history is now accessible ✓" : "Token refreshed — read_all_orders is still not granted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-check failed");
    }
  };
  if (!s?.configured) return null;
  const full = s.all_orders === true;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="shopify-access-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🛍️</span>
            <h2 className="font-semibold text-lg">Shopify Admin API access</h2>
            {s.all_orders === null ? (
              <Badge className="bg-slate-100 text-slate-700 border border-slate-200">Static token</Badge>
            ) : full ? (
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="shopify-access-full">Full order history ✓</Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="shopify-access-60d">Last 60 days only</Badge>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Store <span className="font-mono">{s.store_domain}</span> · API {s.api_version} · {s.auth_mode === "client_credentials" ? "Dev Dashboard app (client credentials)" : "static Admin token"}.
            {!full && s.all_orders === false && (
              <> Claims → <b>New claim</b> and <b>Re-sync from Shopify</b> can only find orders from the <b>last 60 days</b>. To reach older orders, add the{" "}
                <span className="font-mono">read_all_orders</span> scope to the CRM app in your Shopify Dev Dashboard (Configuration → Access scopes → add it next to{" "}
                <span className="font-mono">write_orders</span> → Release), approve the new permission when Shopify asks, then press <b>Re-check</b> here.</>
            )}
          </p>
          {s.scopes && (
            <div className="text-[11px] text-slate-500 mt-2 font-mono break-words" data-testid="shopify-access-scopes">scopes: {s.scopes.join(", ")}</div>
          )}
          {s.error && <div className="text-xs text-red-700 mt-2">{s.error}</div>}
        </div>
        {s.auth_mode === "client_credentials" && (
          <Button variant="outline" onClick={recheck} disabled={isFetching} data-testid="shopify-access-recheck">🔄 Re-check</Button>
        )}
      </div>
    </div>
  );
};
